import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts';

import SeatsLegend from '@/components/atoms/Chart/SeatsLegend/SeatsLegend';
import { Colors } from '@/components/atoms/Chart/Colors';

const BAR_SIZE = 22;

interface SeatsDatum {
  label: string;
  purchased: number;
  active: number;
  new: number;
  inactive: number;
}
type Variant = 'full' | 'minimal';
type SeatType = 'Internal' | 'External';

interface BarChartProps {
  data: SeatsDatum[];
  legendTotals?: { available: number; active: number; new: number; inactive: number };
  height?: number;
  colors?: Partial<typeof Colors>;
  className?: string;
  variant?: Variant;
  showTooltip?: boolean;
  showLegend?: boolean;
  /** Which seat bucket this chart shows; sets the active legend label. */
  seatType?: SeatType;
}

const DEFAULT_HEIGHT = 160;

/* Only active and new users consume a seat; inactive users don't. */
const remainingSeats = (d: SeatsDatum): number => Math.max(0, d.purchased - d.active - d.new);

const BarChart: React.FC<BarChartProps> = ({
  data,
  legendTotals,
  height = DEFAULT_HEIGHT,
  colors,
  className = '',
  variant = 'full',
  showTooltip,
  showLegend,
  seatType,
}) => {
  const palette = useMemo(() => ({ ...Colors, ...colors }), [colors]);

  const derivedTotals = useMemo(() => {
    if (legendTotals != null) return legendTotals;
    const [d] = data;
    if (data.length === 0) return { available: 0, active: 0, new: 0, inactive: 0 };
    return { available: remainingSeats(d), active: d.active, new: d.new, inactive: d.inactive };
  }, [data, legendTotals]);

  const stacked = useMemo(() => data.map((d) => ({ ...d, remaining: remainingSeats(d) })), [data]);

  const isMinimal = variant === 'minimal';
  const tooltipOn = showTooltip ?? !isMinimal;
  const legendOn = showLegend ?? true;

  const activeLabel = seatType != null ? `Active ${seatType} Users` : 'Active Users';

  const legendItems = useMemo(
    () => [
      { label: 'Seats Available', color: palette.seatAvailable, value: derivedTotals.available },
      { label: activeLabel, color: palette.seatActive, value: derivedTotals.active },
      { label: 'New User Seats', color: palette.seatNew, value: derivedTotals.new },
      { label: 'Inactive User Seats', color: palette.seatInactive, value: derivedTotals.inactive },
    ],
    [derivedTotals, palette, activeLabel]
  );

  return (
    <div className={className}>
      {/* Own the height here so ResponsiveContainer has a measured box */}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ReBarChart
            data={stacked}
            layout="vertical"
            margin={{ top: 0, right: 30, bottom: 8, left: 8 }}
            barCategoryGap={0}
          >
            <CartesianGrid horizontal={!isMinimal} vertical={false} />
            <XAxis type="number" hide={isMinimal} />
            <YAxis
              type="category"
              dataKey="label"
              tickLine={false}
              axisLine={false}
              hide={isMinimal}
            />
            {tooltipOn && <Tooltip />}

            <Bar
              dataKey="active"
              stackId="usage"
              fill={palette.seatActive}
              barSize={BAR_SIZE}
              radius={0}
            />
            <Bar dataKey="new" stackId="usage" fill={palette.seatNew} barSize={BAR_SIZE} />
            <Bar
              dataKey="inactive"
              stackId="usage"
              fill={palette.seatInactive}
              barSize={BAR_SIZE}
            />
            <Bar
              dataKey="remaining"
              stackId="usage"
              fill={palette.seatAvailable}
              barSize={BAR_SIZE}
              radius={0}
            >
              <LabelList dataKey="purchased" position="right" offset={8} />
            </Bar>
          </ReBarChart>
        </ResponsiveContainer>
      </div>

      {legendOn && (
        <div style={{ marginTop: 12 }}>
          <SeatsLegend items={legendItems} />
        </div>
      )}
    </div>
  );
};

export default BarChart;
